/**
 * @fileoverview Visibility Manager - ページ可視性管理（最小構成）
 * バックグラウンド遷移を検知し、タイマーやアニメーションの異常を防止
 */

/**
 * ページ可視性管理クラス
 * - visibilitychange イベントのみ使用（シンプル）
 * - 重複発火を抑止
 * - コールバック実行時のエラーを隔離
 */
export class VisibilityManager {
  constructor() {
    this.isVisible = !document.hidden;
    this._lastState = this.isVisible ? 'visible' : 'hidden';
    this.pauseCallbacks = new Set();
    this.resumeCallbacks = new Set();
    this._handleVisibilityChange = this._handleVisibilityChange.bind(this);
  }

  /**
   * 初期化：visibilitychangeイベントを監視
   */
  initialize() {
    document.addEventListener('visibilitychange', this._handleVisibilityChange, { passive: true });
  }

  /**
   * バックグラウンド遷移時のコールバック登録
   * @param {Function} callback - 実行する関数
   * @returns {Function} 解除用関数
   */
  onBackgrounded(callback) {
    this.pauseCallbacks.add(callback);
    return () => this.pauseCallbacks.delete(callback);
  }

  /**
   * フォアグラウンド復帰時のコールバック登録
   * @param {Function} callback - 実行する関数
   * @returns {Function} 解除用関数
   */
  onResumed(callback) {
    this.resumeCallbacks.add(callback);
    return () => this.resumeCallbacks.delete(callback);
  }

  /**
   * visibilitychange イベントハンドラ
   * @private
   */
  _handleVisibilityChange() {
    const nextState = document.hidden ? 'hidden' : 'visible';

    // 重複抑止：同じ状態への遷移は無視
    if (nextState === this._lastState) return;

    this._lastState = nextState;
    this.isVisible = nextState === 'visible';

    // コールバック実行（エラー隔離）
    const callbacks = this.isVisible ? this.resumeCallbacks : this.pauseCallbacks;
    callbacks.forEach((callback) => {
      try {
        callback();
      } catch (error) {
        // コールバックのエラーが他のコールバックに影響しないよう隔離
        console.warn('VisibilityManager callback error:', error);
      }
    });
  }

  /**
   * クリーンアップ
   */
  destroy() {
    document.removeEventListener('visibilitychange', this._handleVisibilityChange);
    this.pauseCallbacks.clear();
    this.resumeCallbacks.clear();
  }
}

// シングルトンインスタンス
export const visibilityManager = new VisibilityManager();
